import { ArrowRight, CheckCircle, Circle, Flask } from '@phosphor-icons/react';
import type { OnboardingChecklist } from '@brechoflow/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useConfirm } from '@/components/confirm-dialog';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { toast } from '@/components/ui/toaster';
import { useSession } from '@/features/auth/session';
import { errorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import { onboardingApi, sqk } from './api';

type Step = OnboardingChecklist['steps'][number];

function useOnboarding() {
  const { membership } = useSession();
  const orgId = membership?.organizationId;
  const qc = useQueryClient();
  const q = useQuery({ queryKey: sqk.onboarding(orgId), queryFn: onboardingApi.get, retry: false });
  const patch = useMutation({
    mutationFn: onboardingApi.patch,
    onSuccess: (data) => qc.setQueryData(sqk.onboarding(orgId), data ?? undefined),
    onSettled: () => qc.invalidateQueries({ queryKey: sqk.onboarding(orgId) }),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const demo = useMutation({
    mutationFn: (load: boolean) => (load ? onboardingApi.loadDemo() : onboardingApi.removeDemo()),
    onSuccess: async (_d, load) => {
      // dados de demonstração mudam listas de várias telas
      await qc.invalidateQueries();
      toast.success(load ? 'Dados de demonstração carregados' : 'Dados de demonstração removidos');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return { q, patch, demo };
}

/** Checklist da Home: some quando concluído ou dispensado; permite pular etapas e gerenciar dados de demonstração. */
export function OnboardingCard() {
  const { q, patch, demo } = useOnboarding();
  const confirm = useConfirm();
  const data = q.data;
  if (!data) return null;
  const hasDemo = data.demoData;
  const finished = data.doneCount + data.steps.filter((s) => s.skipped && !s.done).length >= data.total;
  if (data.state.dismissed || (finished && !hasDemo)) return null;

  const skip = (s: Step) => patch.mutate({ skippedSteps: [...new Set([...data.state.skippedSteps, s.key])] });
  const onDemo = async (load: boolean) => {
    const ok = await confirm(
      load
        ? { title: 'Carregar dados de demonstração?', description: 'Cria clientes e outros exemplos para você explorar o sistema. Eles ficam marcados e podem ser removidos a qualquer momento.', confirmLabel: 'Carregar dados' }
        : { title: 'Remover dados de demonstração?', description: 'Apaga somente os exemplos criados pela demonstração. Os dados que você cadastrou não são alterados.', confirmLabel: 'Remover', destructive: true },
    );
    if (ok) demo.mutate(load);
  };

  return (
    <section aria-labelledby="setup-title" data-testid="setup-checklist" className="mb-5 rounded-panel border bg-sand/50 px-4 py-3 sm:px-5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4">
        <h2 id="setup-title" className="font-display text-base font-semibold">
          Deixe a loja pronta para atender
          <span className="ml-2 text-sm font-normal text-muted-foreground tabular">
            {data.doneCount} de {data.total} etapas
          </span>
        </h2>
        {!finished && (
          <button type="button" className="text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground" onClick={() => patch.mutate({ dismissed: true })}>
            Dispensar
          </button>
        )}
      </div>
      {!finished && (
        <ul className="mt-1 grid list-none gap-x-6 p-0 sm:grid-cols-2 lg:grid-cols-3">
          {data.steps.map((s) => (
            <li key={s.key} className="flex items-center gap-1">
              <Link to={s.href} className={cn('group flex min-h-11 min-w-0 flex-1 items-center gap-2 text-sm hover:text-primary', (s.done || s.skipped) && 'text-muted-foreground')}>
                {s.done ? <CheckCircle className="size-4 shrink-0 text-success" weight="fill" aria-label="Concluída" /> : <Circle className="size-4 shrink-0 text-muted-foreground" aria-hidden />}
                <span className={cn('min-w-0 flex-1', s.done && 'line-through')}>
                  {s.label}
                  {s.skipped && !s.done && <span> (pulada)</span>}
                </span>
                <ArrowRight className="size-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" aria-hidden />
              </Link>
              {!s.done && !s.skipped && (
                <button type="button" aria-label={`Pular etapa: ${s.label}`} className="shrink-0 rounded-control px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground" onClick={() => skip(s)}>
                  Pular
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2 border-t pt-2">
        <Flask className="size-4 text-muted-foreground" aria-hidden />
        <span className="text-xs text-muted-foreground">{hasDemo ? 'Há dados de demonstração na loja.' : 'Quer explorar com exemplos?'}</span>
        <Button variant="ghost" size="sm" loading={demo.isPending} onClick={() => void onDemo(!hasDemo)}>
          {hasDemo ? 'Remover dados de demonstração' : 'Carregar dados de demonstração'}
        </Button>
      </div>
    </section>
  );
}

/** Assistente em modal para loja nova: leva o usuário aos passos pendentes. Aparece uma vez (wizardCompleted). */
export function OnboardingWizard() {
  const { q, patch } = useOnboarding();
  const navigate = useNavigate();
  const [closed, setClosed] = useState(false);
  const data = q.data;
  if (!data || data.state.wizardCompleted || data.state.dismissed || closed) return null;
  const pending = data.steps.filter((s) => !s.done && !s.skipped);
  if (pending.length === 0) return null;

  const finish = (href?: string) => {
    setClosed(true);
    patch.mutate({ wizardCompleted: true });
    if (href) navigate(href);
  };

  return (
    <Modal
      open
      onOpenChange={(o) => !o && finish()}
      title="Bem-vindo! Vamos configurar sua loja"
      description="Siga estes passos para começar a atender. Você pode voltar a eles pelo checklist da página inicial."
      footer={
        <>
          <Button variant="secondary" onClick={() => finish()}>
            Fazer depois
          </Button>
          <Button onClick={() => finish(pending[0]!.href)}>
            Começar
            <ArrowRight className="size-4" aria-hidden />
          </Button>
        </>
      }
    >
      <ol className="m-0 flex list-none flex-col gap-2 p-0" data-testid="wizard-steps">
        {data.steps.map((s, i) => (
          <li key={s.key}>
            <button
              type="button"
              disabled={s.done}
              onClick={() => finish(s.href)}
              className="flex min-h-11 w-full items-center gap-3 rounded-control border px-3 text-left text-sm hover:bg-muted disabled:opacity-60 disabled:hover:bg-transparent"
            >
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/12 text-xs font-semibold text-primary">{s.done ? <CheckCircle className="size-5" weight="fill" aria-label="Concluída" /> : i + 1}</span>
              <span className="min-w-0 flex-1">{s.label}</span>
              {!s.done && <ArrowRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />}
            </button>
          </li>
        ))}
      </ol>
    </Modal>
  );
}
